import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Button,
  IconButton,
  Notice,
  OtpField,
  StateScreen,
  TextField,
  iconBack,
} from '@sauda/ui/kit';
import { useAuth } from '../auth/AuthContext.js';
import { ApiError, api } from '../lib/api.js';
import { BrandLogo } from '../app/BrandLogo.js';

interface VerifyResponse {
  accessToken: string;
  agencyId: string | null;
  memberships: unknown[];
}

/** Only follow same site paths after sign in, never an address a link supplied. */
export function safeNext(value: string | null): string | null {
  return value !== null && value.startsWith('/') && !value.startsWith('//') ? value : null;
}

/** `14:32` from seconds. */
export function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${String(Math.floor(s / 60))}:${String(s % 60).padStart(2, '0')}`;
}

const RESEND_SECONDS = 30;

/** Four shapes in one hue family stand in for an illustration, so the first screen costs no bytes. */
function Art() {
  return (
    <div className="sign-art" aria-hidden="true">
      <span className="sign-art__a" />
      <span className="sign-art__b" />
      <span className="sign-art__c" />
      <span className="sign-art__d" />
    </div>
  );
}

export function SignInScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const auth = useAuth();
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'phone' | 'code' | 'locked'>('phone');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | undefined>(undefined);
  const [resendIn, setResendIn] = useState(0);
  const [lockedFor, setLockedFor] = useState(0);
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);
  useEffect(() => {
    if (lockedFor <= 0) return;
    const id = setTimeout(() => setLockedFor((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [lockedFor]);

  const digits = phone.replace(/\D/g, '').replace(/^91(?=\d{10}$)/, '');
  const phoneValid = /^[6-9]\d{9}$/.test(digits);
  // A full length number that cannot be right is said so as it is typed.
  const shownPhoneError =
    phoneError ?? (digits.length >= 10 && !phoneValid ? t('signin.badPhone') : undefined);

  const lock = (e: ApiError) => {
    const seconds = Number(
      (e.details as { retryAfterSeconds?: number } | undefined)?.retryAfterSeconds ?? 900,
    );
    setLockedFor(seconds);
    setStep('locked');
  };

  const requestCode = async (): Promise<boolean> => {
    setBusy(true);
    setError(null);
    setOffline(false);
    try {
      await api('/auth/otp/request', { method: 'POST', body: { phone: digits }, auth: false });
      setResendIn(RESEND_SECONDS);
      return true;
    } catch (e) {
      if (e instanceof ApiError) {
        if (
          e.status === 429 &&
          (e.details as { retryAfterSeconds?: number } | undefined)?.retryAfterSeconds !== undefined
        )
          lock(e);
        else
          setError(
            e.status === 429
              ? t('signin.tooMany')
              : e.status === 400
                ? t('signin.badPhone')
                : e.status === 503
                  ? t('signin.sendFailed')
                  : t('signin.generic'),
          );
      } else {
        setOffline(true);
      }
      return false;
    } finally {
      setBusy(false);
    }
  };

  const onPhone = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!phoneValid) {
      setPhoneError(t('signin.badPhone'));
      return;
    }
    setPhoneError(undefined);
    if (await requestCode()) setStep('code');
  };

  const verify = async (value: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api<VerifyResponse>('/auth/otp/verify', {
        method: 'POST',
        body: { phone: digits, code: value },
        auth: false,
      });
      await auth.adopt(result.accessToken);
      const next = safeNext(params.get('next'));
      if (result.memberships.length === 0 && next === null)
        void navigate('/onboarding', { replace: true });
      else void navigate(next ?? '/', { replace: true });
    } catch (e) {
      setCode('');
      if (e instanceof ApiError) {
        if (e.status === 429) lock(e);
        else if (e.code === 'CODE_EXPIRED') setError(t('signin.codeExpired'));
        else if (e.status === 401) {
          const left = (e.details as { attemptsLeft?: number } | undefined)?.attemptsLeft;
          setError(
            typeof left === 'number' ? t('signin.triesLeft', { count: left }) : t('signin.badCode'),
          );
        } else setError(t('signin.generic'));
      } else {
        setOffline(true);
      }
    } finally {
      setBusy(false);
    }
  };

  if (step === 'locked') {
    return (
      <main className="sign-page">
        <StateScreen
          kind="locked"
          title={t('signin.lockedTitle')}
          action={
            <Button
              variant="tonal"
              size="lg"
              onClick={() => {
                setStep('phone');
                setCode('');
                setError(null);
              }}
            >
              {t('signin.anotherNumber')}
            </Button>
          }
        >
          <p>{t('signin.lockedBody')}</p>
          <p className="k-t-body-lg">{t('signin.tryAgainIn')}</p>
          <p className="k-t-display-xl" aria-live="off">
            {clock(lockedFor)}
          </p>
        </StateScreen>
      </main>
    );
  }

  return (
    <main className="sign-page">
      {step === 'code' ? (
        <IconButton
          icon={iconBack}
          label={t('app.back')}
          onClick={() => {
            setStep('phone');
            setCode('');
            setError(null);
          }}
        />
      ) : (
        <Art />
      )}
      {step === 'phone' ? (
        <form className="sign-inner" onSubmit={(e) => void onPhone(e)} noValidate>
          <div>
            <h1 className="sign-logo">
              <BrandLogo />
            </h1>
            <p className="k-t-body-lg k-muted">{t('signin.tagline')}</p>
          </div>
          <TextField
            label={t('signin.phone')}
            name="phone"
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            prefix="+91"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={shownPhoneError}
            required
          />
          {offline ? (
            <Notice
              tone="error"
              title={t('signin.offlineTitle')}
              action={
                <Button
                  variant="text"
                  onClick={() => void requestCode().then((ok) => ok && setStep('code'))}
                >
                  {t('app.retry')}
                </Button>
              }
            >
              {t('signin.offlineBody')}
            </Notice>
          ) : null}
          {error === null ? null : <Notice tone="error">{error}</Notice>}
          <div className="sign-page__spacer" />
          <Button type="submit" size="lg" block loading={busy} disabled={!phoneValid}>
            {t('signin.sendCode')}
          </Button>
        </form>
      ) : (
        <div className="sign-inner">
          <div>
            <h1 className="k-t-headline">{t('signin.codeTitle')}</h1>
            <p className="k-t-body-lg k-muted">
              {t('signin.sentTo', { phone: `+91 ${digits.slice(0, 5)} ${digits.slice(5)}` })}{' '}
              <Button
                variant="text"
                onClick={() => {
                  setStep('phone');
                  setCode('');
                  setError(null);
                }}
              >
                {t('signin.change')}
              </Button>
            </p>
          </div>
          <OtpField
            label={t('signin.code')}
            labelHidden
            value={code}
            onChange={setCode}
            onComplete={(v) => void verify(v)}
            error={error ?? undefined}
            disabled={busy}
            autoFocus
          />
          {offline ? (
            <Notice tone="error" title={t('signin.offlineTitle')}>
              {t('signin.offlineBody')}
            </Notice>
          ) : null}
          <p className="k-t-label-sm k-muted">
            {resendIn > 0 ? (
              t('signin.resendIn', { time: clock(resendIn) })
            ) : (
              <Button variant="text" disabled={busy} onClick={() => void requestCode()}>
                {t('signin.resend')}
              </Button>
            )}
          </p>
          <div className="sign-page__spacer" />
          <Button
            size="lg"
            block
            loading={busy}
            disabled={code.length !== 6}
            onClick={() => void verify(code)}
          >
            {t('signin.signIn')}
          </Button>
        </div>
      )}
    </main>
  );
}
